import React, { useCallback, useState, useEffect } from "react";
import { Grid, Stack, Typography, Paper } from "@mui/material";
import { ErrorAlert, TestEnvironmentChip } from "../../../utils";
import { PatientForm, DiagnosisForm, TypeSelector } from "./form";
import StepWizard from "react-step-wizard";
import WizardStep from "../../../utils/wizard/step";
import transitions from "./transitions.less";
import _ from "lodash";
import { useSearchParams } from "react-router-dom";
import { useApi, useIsInQuickLink, useNavigate } from "../../../utils/hooks";

const transitionsOptions = {
  enterRight: `${transitions.animated} ${transitions.enterRight}`,
  enterLeft: `${transitions.animated} ${transitions.enterLeft}`,
  exitRight: `${transitions.animated} ${transitions.exitRight}`,
  exitLeft: `${transitions.animated} ${transitions.exitLeft}`,
  intro: `${transitions.animated} ${transitions.intro}`,
};

const EmptyStep = () => null;

const TYPE_STEPS_COUNT = 3;

const TYPE_SELECTOR_STEP = 2;

const GENERIC_TITLE = "Nueva prescripción";

const pickPatientData = (form) => _.pick(form, ["patient", "source"]);

const NewMedicalDocument = ({ modules }) => {
  const hasTypeSelection = modules.length > 1;
  const [selectedBasePath, setSelectedBasePath] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const selectedModule = hasTypeSelection
    ? _.find(modules, { basePath: selectedBasePath })
    : _.first(modules);
  const [createStatus, setCreateStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [createdMedicalDocuments, setCreatedMedicalDocuments] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const api = useApi();
  const [searchParams] = useSearchParams();
  const isInQuickLink = useIsInQuickLink();
  const source = isInQuickLink ? 'web-client-embedded' : 'web-client';

  const onFinish = (partialForm) =>
    setForm((form) => ({ ...form, ...partialForm, source }));
  const onAlertClose = () => setCreateStatus(null);
  const onTypeSelect = (basePath) => {
    if (basePath !== selectedBasePath) setForm(pickPatientData);
    setSelectedBasePath(basePath);
  };

  const create = useCallback(
    (lastForm) => {
      const reference = searchParams.get("reference");
      const formToSubmit = _.assign({}, lastForm, form, { reference });

      if (selectedModule.basePath === "orders" && _.isEmpty(formToSubmit.medicines)) {
        setError(null);
        setCreateStatus("error");
        return;
      }

      setCreateStatus("loading");

      if (selectedModule.basePath === "orders") {
        const promises = formToSubmit.medicines.map((medicineItem) => {
          const singleMedicineForm = {
            ...formToSubmit,
            medicine: medicineItem.text,
            orderTemplate: { id: _.get(medicineItem.template, "id") },
            orderType: medicineItem.orderType ?? formToSubmit.orderType,
            items: medicineItem.items,
            notes: medicineItem.notes,
            // TODO: stop sending clarifications once api drops the alias (keeps notes on an api rollback)
            clarifications: medicineItem.notes,
          };
          delete singleMedicineForm.medicines;

          return api.createOrder(singleMedicineForm)
        });
        Promise.all(promises)
          .then((responses) => {
            setCreateStatus("success");
            setCreatedMedicalDocuments(responses);
            return;
          })
          .catch((error) => {
            setCreateStatus("error");
            setError(error);
          });
      } else {
        api.createPrescription(formToSubmit)
          .tap(({ recurring, ...createdPrescription }) => {
            const createdMedicalDocuments =
              !_.isEmpty(recurring)
                ? _(recurring).map(recurringProps => _.assign({}, createdPrescription, recurringProps)).thru(recurringPrescriptions => _.concat(createdPrescription, recurringPrescriptions)).value()
                : [createdPrescription];

            setCreatedMedicalDocuments(createdMedicalDocuments);
            setCreateStatus("success");
          })
          .catch((error) => {
            setCreateStatus("error");
            setError(error);
          });
      }
    },
    [form, selectedModule, searchParams]
  );

  const isLoading = createStatus === "loading";
  const isChoosingType = hasTypeSelection && activeStep <= TYPE_SELECTOR_STEP;
  const title = isChoosingType || !selectedModule ? GENERIC_TITLE : `Nueva ${selectedModule.name}`;
  const typeSteps = selectedModule
    ? [
        <selectedModule.MedicineForm key={`${selectedModule.basePath}-medicine`} onFinish={onFinish} fullForm={form} />,
        <DiagnosisForm key={`${selectedModule.basePath}-diagnosis`} onFinish={onFinish} />,
        <selectedModule.DateForm
          key={`${selectedModule.basePath}-date`}
          onFinish={create}
          isLoading={isLoading}
          fullForm={form}
        />,
      ]
    : _.times(TYPE_STEPS_COUNT, (index) => <EmptyStep key={`empty-${index}`} />);
  const failed = createStatus === "error";
  const errorMessage = _.get(error, "response.data.message");

  useEffect(() => {
    if (createdMedicalDocuments && createStatus === "success") {
      const id = _.first(createdMedicalDocuments).id;
      navigate(`/details-document/${id}`, {
        state: {
          createdMedicalDocuments: createdMedicalDocuments,
          showSuccess: true,
          backPath: "/"
        },
      });
    }
  }, [createdMedicalDocuments, navigate, createStatus]);

  return (
    <Grid width="100%">
      <Stack
        spacing="1rem"
        sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" } }}
      >
        <Stack direction="row" columnGap={1.5} rowGap={0.5} alignItems="center" flexWrap="wrap">
          <Typography variant="titleSteps" sx={{ fontSize: { xs: 20, sm: 24 } }}>
            {title}
            {form?.patient ? (
              <>
                {" | "}
                <Typography
                  sx={{
                    display: "inline",
                    color: "secondary.main",
                    fontSize: { xs: 14, sm: 24 },
                  }}
                >
                  {`${form?.patient?.name}, ${form?.patient?.surname}`}
                </Typography>
              </>
            ) : null}
          </Typography>
          <TestEnvironmentChip />
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          alignItems="center"
          spacing="2rem"
          width="100%"
        >
          <div style={{ width: "100%" }}>
            <Paper
              variant="outlined"
              width="100%"
              sx={{
                p: { xs: "20px 10px", sm: "20px 40px" },
                borderRadius: 3,
                mb: 7,
              }}
            >
              {!createdMedicalDocuments && (
                <StepWizard
                  transitions={transitionsOptions}
                  onStepChange={({ activeStep }) => setActiveStep(activeStep)}
                >
                  <WizardStep>
                    <PatientForm onFinish={onFinish} />
                  </WizardStep>
                  {hasTypeSelection && (
                    <WizardStep>
                      <TypeSelector
                        modules={modules}
                        selectedBasePath={selectedBasePath}
                        onSelect={onTypeSelect}
                      />
                    </WizardStep>
                  )}
                  {typeSteps.map((step) => (
                    <WizardStep key={step.key}>{step}</WizardStep>
                  ))}
                </StepWizard>
              )}
            </Paper>
          </div>
        </Stack>
        <ErrorAlert
          show={failed}
          onClose={onAlertClose}
          message={`No se pudo crear la ${selectedModule?.name} ${
            errorMessage ? `(${errorMessage})` : ""
          }`}
        />
      </Stack>
    </Grid>
  );
};

export default NewMedicalDocument;
